import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Eye, Trash2, Plus, Copy, Loader2, Car, Hash, Link as LinkIcon, RefreshCw,
} from "lucide-react";
import { usePdfPreview } from "@/components/PdfPreviewDialog";
import { VehicleQuoteBlock } from "@/components/VehicleQuoteBlock";
import { ProposalSettings } from "@/components/ProposalSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake } from "lucide-react";

export default function ProposalEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [addUrls, setAddUrls] = useState("");
  const [addVins, setAddVins] = useState("");
  const [addMode, setAddMode] = useState("urls");   // "urls" | "vins"
  const [adding, setAdding] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [title, setTitle] = useState("");
  const [dealership, setDealership] = useState("");
  const [dealers, setDealers] = useState([]);
  const [requoting, setRequoting] = useState(false);

  const load = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get(`/proposals/${id}`),
        api.get("/dealers").catch(() => ({ data: [] })),
      ]);
      setP(pRes.data);
      setTitle(pRes.data.title);
      setDealership(pRes.data.dealership || "");
      setDealers(dRes?.data || []);
    } catch {
      toast.error("Proposal not found");
      nav("/dashboard");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const parseUrls = () =>
    addUrls.split(/[\s\n,]+/).map((s) => s.trim()).filter((s) => /^(https?:\/\/|vin:)/i.test(s));

  // Parse a "paste VINs" textarea into synthetic `vin:VIN?mileage=…` tokens.
  // Each non-empty line: `VIN` OR `VIN,mileage` OR `VIN mileage`.
  const parseVinLines = () => {
    const tokens = [];
    for (const raw of (addVins || "").split(/\n+/)) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(/[\s,;|\t]+/).filter(Boolean);
      const vin = (parts[0] || "").toUpperCase();
      if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin)) continue;
      const miles = parts.slice(1).map((p) => p.replace(/[^\d]/g, "")).find((p) => p) || "";
      tokens.push(miles ? `vin:${vin}?mileage=${miles}` : `vin:${vin}`);
    }
    return tokens;
  };

  const currentTokens = addMode === "vins" ? parseVinLines() : parseUrls();

  const doAdd = async () => {
    const urls = currentTokens;
    if (urls.length === 0) return;
    if ((p.vehicles?.length || 0) + urls.length > 15) {
      toast.error("Maximum 15 vehicles per proposal");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(`/proposals/${id}/vehicles`, { urls });
      setP(data);
      setAddUrls("");
      setAddVins("");
      toast.success("Vehicles added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const removeVehicle = async (idx) => {
    try {
      await api.delete(`/proposals/${id}/vehicles/${idx}`);
      await load();
      toast.success("Removed");
    } catch {
      toast.error("Failed");
    }
  };

  const saveMeta = async () => {
    try {
      const { data } = await api.put(`/proposals/${id}`, {
        title, dealership: dealership || null,
      });
      setP(data);
      setEditingMeta(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const { preview, dialog: pdfDialog } = usePdfPreview();
  const downloadPdf = () => {
    preview({
      fetcher: () => api.get(`/proposals/${id}/pdf`, { responseType: "blob" }),
      filename: `${(p?.title || "proposal").replace(/[^\w-]+/g, "_")}.pdf`,
      title: `Proposal · ${p?.title || ""}`,
    });
  };

  const copyShare = async () => {
    const shareUrl = `${window.location.origin}/share/${p.share_token}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  // Re-run the rate engine against every stored vehicle. Rescues proposals
  // whose quotes came back empty because the rate sheets weren't loaded on
  // this environment at creation time (e.g. immediately after a fresh
  // deploy). Vehicle bodies stay intact — only the quotes are refreshed.
  const requote = async () => {
    if (requoting) return;
    setRequoting(true);
    try {
      const { data } = await api.post(`/proposals/${id}/requote`);
      setP(data.proposal || data);
      toast.success(`Regenerated ${data.total_quotes || 0} quotes across ${data.vehicles_requoted || 0} vehicles`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Requote failed");
    } finally {
      setRequoting(false);
    }
  };

  if (!p) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-[#6C757D]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Proposal • {p.vehicles?.length || 0} vehicles
            </div>
            {editingMeta ? (
              <div className="mt-3 space-y-2">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="text-2xl font-display font-black rounded-sm" data-testid="edit-title-input" />
                <Input value={dealership} onChange={(e) => setDealership(e.target.value)} placeholder="Dealership" className="rounded-sm" data-testid="edit-dealership-input" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveMeta} className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-meta-button">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingMeta(false)} className="rounded-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <h1
                  className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-tight cursor-pointer hover:text-[#1C2541]"
                  onClick={() => setEditingMeta(true)}
                  data-testid="proposal-heading"
                >
                  {p.title}
                </h1>
                {p.dealership && (
                  <p className="text-sm text-[#6C757D] mt-2">
                    Prepared for <span className="font-semibold text-[#0B132B]">{p.dealership}</span> • by{" "}
                    <span className="font-semibold text-[#0B132B]">{p.agent_name}</span>
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={requote} disabled={requoting} className="rounded-sm border-[#0B132B]/20" data-testid="requote-button" title="Recalculate quotes with the current rate engine">
              {requoting ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
              {requoting ? "Regenerating…" : "Regenerate quotes"}
            </Button>
            <Button variant="outline" onClick={copyShare} className="rounded-sm border-[#0B132B]/20" data-testid="copy-share-button">
              <Copy size={14} className="mr-2" /> Copy share link
            </Button>
            <Link to={`/proposals/${p.id}/view`} target="_blank">
              <Button variant="outline" className="rounded-sm border-[#0B132B]/20" data-testid="view-presentation-button">
                <Eye size={14} className="mr-2" /> View presentation
              </Button>
            </Link>
            <Button onClick={downloadPdf} className="rounded-sm bg-[#D4AF37] hover:bg-[#c39d2e] text-[#0B132B] font-semibold" data-testid="download-pdf-button">
              <Eye size={14} className="mr-2" /> Preview & Download
            </Button>
          </div>
        </div>
        {pdfDialog}

        {/* Ineligible-makes callout — makes on this proposal that returned
            zero quotes because they're not on the Class Guide and have no
            override set. Gives admins a one-click shortcut to add them via
            /admin/rates so the requote pulls prices. */}
        {(p.ineligible_makes || []).length > 0 && (
          <Card
            className="bg-[#FFFBEB] border border-[#F2C94C] rounded-sm p-5 mb-6"
            data-testid="ineligible-makes-callout"
          >
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="shrink-0 w-9 h-9 rounded-sm bg-[#F2C94C] text-[#0B132B] flex items-center justify-center font-bold text-sm">
                !
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                  Class guide gap — pricing skipped
                </div>
                <div className="text-sm text-[#0B132B] mt-1.5">
                  {p.ineligible_makes.map((m, i) => (
                    <span key={m.make}>
                      {i > 0 ? ", " : ""}
                      <strong>{m.make}</strong>{" "}
                      <span className="text-[#6C757D]">({m.vehicle_count}&nbsp;vehicle{m.vehicle_count > 1 ? "s" : ""})</span>
                    </span>
                  ))}{" "}
                  aren't on the Class Guide and have no override set, so no quotes were generated.
                </div>
                <div className="text-xs text-[#6C757D] mt-1">
                  Add a Class Override on <span className="font-mono">Admin&nbsp;→&nbsp;Rates</span>, then click <em>Regenerate quotes</em>.
                </div>
              </div>
              {user?.role === "admin" && (
                <Link
                  to={`/admin/rates?add_make=${encodeURIComponent(p.ineligible_makes[0].make)}#class-overrides`}
                  className="shrink-0"
                >
                  <Button
                    className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white h-9 font-semibold"
                    data-testid="add-class-override-button"
                  >
                    <Plus size={14} className="mr-1.5" /> Add class override
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        )}

        {/* Proposal settings: markup + term filter */}
        <ProposalSettings proposal={p} onChange={(next) => setP(next)} />

        {/* Explicit dealer link — locks the closing ratio to the exact packet */}
        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-5 mb-6" data-testid="proposal-dealer-link-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <Handshake size={16} className="text-[#0B132B]"/>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Linked dealer signup</div>
                <div className="text-[11px] text-[#6C757D]">Ties this proposal to a specific dealer packet for the closing ratio.</div>
              </div>
            </div>
            <div className="flex-1"></div>
            <div className="w-full sm:w-80">
              <Select
                value={p.linked_dealer_id || "__none__"}
                onValueChange={async (v) => {
                  try {
                    const { data } = await api.put(`/proposals/${id}`, { linked_dealer_id: v === "__none__" ? "" : v });
                    setP(data);
                    toast.success(v === "__none__" ? "Link cleared" : "Linked to dealer");
                  } catch { toast.error("Failed to link"); }
                }}
              >
                <SelectTrigger className="h-10 rounded-sm border-[#0B132B]/15" data-testid="link-dealer-select">
                  <SelectValue placeholder="No dealer linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No dealer linked</SelectItem>
                  {dealers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.dealer_name} — {(d.status || "draft").replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Add vehicles */}
        {(p.vehicles?.length || 0) < 15 && (
          <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6 mb-10" data-testid="add-vehicles-card">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                Add more vehicles
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-sm border border-[#E9ECEF] bg-[#F8F9FA] p-0.5">
                  <button type="button" onClick={() => setAddMode("urls")}
                    className={`px-3 py-1 text-[10px] uppercase tracking-[0.16em] font-bold rounded-sm inline-flex items-center gap-1.5 ${addMode === "urls" ? "bg-[#0B132B] text-white" : "text-[#6C757D]"}`}
                    data-testid="add-mode-urls">
                    <LinkIcon size={11} /> URLs
                  </button>
                  <button type="button" onClick={() => setAddMode("vins")}
                    className={`px-3 py-1 text-[10px] uppercase tracking-[0.16em] font-bold rounded-sm inline-flex items-center gap-1.5 ${addMode === "vins" ? "bg-[#0B132B] text-white" : "text-[#6C757D]"}`}
                    data-testid="add-mode-vins">
                    <Hash size={11} /> Paste VINs
                  </button>
                </div>
                <div className="text-xs text-[#6C757D] font-mono">{p.vehicles?.length || 0}/15</div>
              </div>
            </div>
            {addMode === "urls" ? (
              <>
                <Textarea
                  value={addUrls}
                  onChange={(e) => setAddUrls(e.target.value)}
                  placeholder="Paste additional vehicle URLs, one per line"
                  rows={3}
                  className="rounded-sm font-mono text-xs mb-3"
                  data-testid="add-vehicles-textarea"
                />
                <p className="text-[11px] text-[#6C757D] mb-3 leading-relaxed">
                  Bulk-scraper handles VDPs + inventory listing pages. If a dealer blocks scraping
                  (DataDome / Cloudflare Ultra), switch to <b>Paste VINs</b> — the NHTSA decoder
                  works on every VIN regardless of the source site.
                </p>
              </>
            ) : (
              <>
                <Textarea
                  value={addVins}
                  onChange={(e) => setAddVins(e.target.value)}
                  placeholder={`1FTFW1E56LFA12345\n5FNRL6H72LB012345 45000\n1G1YY26U165123456, 82000`}
                  rows={5}
                  className="rounded-sm font-mono text-xs mb-3"
                  data-testid="add-vehicles-vins-textarea"
                />
                <p className="text-[11px] text-[#6C757D] mb-3 leading-relaxed">
                  One 17-character VIN per line. Optional mileage after a space or comma:{" "}
                  <code className="text-[10px] bg-[#F1F3F5] px-1 rounded">VIN 45000</code>. We decode
                  each via NHTSA — free, works on every dealer including DataDome-protected sites.
                  <b className="ml-1">Detected: {currentTokens.length} valid VIN{currentTokens.length === 1 ? "" : "s"}.</b>
                </p>
              </>
            )}
            <Button
              onClick={doAdd}
              disabled={adding || currentTokens.length === 0}
              className="rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white"
              data-testid="add-vehicles-button"
            >
              {adding ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</>) : (<><Plus size={14} className="mr-2" /> Add {currentTokens.length > 0 && `(${currentTokens.length})`} & quote</>)}
            </Button>
          </Card>
        )}

        {/* Vehicle blocks */}
        <div className="space-y-6" data-testid="vehicle-list">
          {p.vehicles?.length === 0 && (
            <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-12 text-center rounded-sm">
              <Car className="mx-auto text-[#D4AF37]" size={32} />
              <p className="text-sm text-[#6C757D] mt-3">No vehicles yet — add some above.</p>
            </Card>
          )}
          {p.vehicles?.map((vq, idx) => (
            <VehicleQuoteBlock
              key={idx}
              index={idx}
              vq={vq}
              markup={p.markup}
              categoryMarkups={p.category_markups}
              gapSettings={p.gap_settings}
              vscDealerType={p.vsc_dealer_type}
              selectedTerms={p.selected_terms}
              selectedProducts={p.selected_products}
              proposalId={p.id}
              onVehicleUpdated={(next) => setP(next)}
              onRemove={() => removeVehicle(idx)}
            />
          ))}
        </div>
      </main>
    </div>
  );
}
